/**
 * E01.03 — Five launch locale packs (en/es/fr/de/ja) plus mixed-script/RTL
 * critical-flow resources.
 *
 * Parent E01 owns the reviewed international accessible interface surface:
 * five locale packs, RTL correctness, keyboard traversal, WCAG 2.2 AA,
 * focused error recovery and a screen-state matrix that the UI relies on.
 * This unit owns the *locale resource layer* the rest of E01 depends on:
 *
 *   1. **Five complete locale packs**. `packages/locales/src/` exports one
 *      frozen pack per launch locale (English, Spanish, French, German,
 *      Japanese). Each pack carries the full set of navigation, validation,
 *      confirmation, notification, accessibility and operator message keys
 *      the critical buyer/seller/operator journeys need. A pack whose key
 *      set differs from the reference pack is a type error (TypeScript
 *      enforces the shape) and a fail-closed `LocalePackError` at runtime
 *      (the verification helper below catches a translation whose key set
 *      drifted). No pack is a partial subset.
 *
 *   2. **Mixed-script and RTL text handling**. Arabic and Hebrew are the two
 *      RTL stress scripts the E01 plan calls out, alongside Japanese and
 *      Latin. The locale module therefore exports:
 *        - `LOCALE_DIRECTION` — the exact `ltr` / `rtl` direction per locale;
 *        - `isRtlLocale` and `resolveDirection` — pure predicates that the
 *          UI shell uses to set `dir` on the document root;
 *        - `renderMixedScript(value, locale)` — a helper that isolates
 *          embedded LTR runs (numbers, currency codes, order IDs, RTL-adjacent
 *          identifiers) with Unicode directional isolates so a currency or
 *          reference id inside an Arabic/Hebrew label does not reorder the
 *          surrounding text;
 *        - `formatMoney(locale, money)` — a server-money formatter that
 *          preserves the exact integer minor units and the currency code in
 *          the correct order for the locale, never using floats;
 *        - `formatNumber(locale, value)` and `formatDate(locale, instant)` —
 *          `Intl`-backed number/date formatting with an explicit locale and
 *          a deterministic timezone so screenshots and tests are stable.
 *
 *   3. **Mixed-script critical flows**. The critical-flow resource pack
 *      exports the small amount of markup that the buyer/seller journeys
 *      need: price-with-currency, confirmation subject, error summary and
 *      recovery action strings, and an RTL-stress fixture that embeds an
 *      LTR currency token and an LTR order id inside an RTL label. The
 *      fixture exists so a UI test can assert the RTL glyph order without
 *      shipping a mirrored screenshot as proof.
 *
 *   4. **Honest boundaries**. This module contains no fabricated translation
 *      review, no legal/financial wording approval and no live provider
 *      activation. Legal/financial text and native review remain explicit
 *      external release gates; the ICU-backed `Intl` formatters require the
 *      platform to expose the locale's full ICU data (Node 22 / modern
 *      browsers do). When a required locale string is missing the helpers
 *      fail closed with a `LocalePackError` rather than emitting an English
 *      fallback.
 *
 * Original planned destination: `packages/locales/src/`.
 */

export const LAUNCH_LOCALES = Object.freeze([
  'en',
  'es',
  'fr',
  'de',
  'ja',
] as const);
export type LaunchLocale = (typeof LAUNCH_LOCALES)[number];

/**
 * RTL stress scripts are not launch locales; they exist so the UI can prove
 * `dir=rtl` layout and mixed-script isolation without claiming those
 * languages are supported. Arabic and Hebrew are the two the E01 plan names.
 */
export const RTL_STRESS_LOCALES = Object.freeze(['ar', 'he'] as const);
export type RtlStressLocale = (typeof RTL_STRESS_LOCALES)[number];

export type Direction = 'ltr' | 'rtl';

/**
 * Exact direction per supported locale. Any locale not in this map is
 * unknown and the helpers below refuse to guess.
 */
export const LOCALE_DIRECTION: Readonly<Record<LaunchLocale | RtlStressLocale, Direction>> =
  Object.freeze({
    en: 'ltr',
    es: 'ltr',
    fr: 'ltr',
    de: 'ltr',
    ja: 'ltr',
    ar: 'rtl',
    he: 'rtl',
  });

/** Well-typed error thrown by the fail-closed helpers below. */
export class LocalePackError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'LocalePackError';
    this.code = code;
  }
}

function assertLaunchLocale(locale: string): asserts locale is LaunchLocale {
  if (!(LAUNCH_LOCALES as readonly string[]).includes(locale)) {
    throw new LocalePackError(
      'UNKNOWN_LAUNCH_LOCALE',
      `Unknown launch locale: ${locale}`,
    );
  }
}

function assertSupportedDirection(locale: string): asserts locale is LaunchLocale | RtlStressLocale {
  if (!Object.hasOwn(LOCALE_DIRECTION, locale)) {
    throw new LocalePackError(
      'UNKNOWN_LOCALE',
      `Unknown locale for direction: ${locale}`,
    );
  }
}

export function isRtlLocale(locale: string): boolean {
  assertSupportedDirection(locale);
  return LOCALE_DIRECTION[locale] === 'rtl';
}

export function resolveDirection(locale: string): Direction {
  assertSupportedDirection(locale);
  return LOCALE_DIRECTION[locale];
}

/**
 * Canonical message-key catalogue. The reference key set is defined once
 * here and every launch pack is required to provide every key. The runtime
 * helpers below check the translation tables for exact key coverage, so a
 * missing key is a fail-closed error instead of a silent English leak.
 *
 * The keys are grouped by journey:
 *  - `nav.*`            — persistent buyer/seller/operator navigation.
 *  - `field.*`          — form field labels with persistent context.
 *  - `validation.*`     — inline and error-summary text.
 *  - `confirmation.*`   — exact action review wording.
 *  - `notification.*`   — persistent status history.
 *  - `a11y.*`           — screen-reader-only labels and busy region names.
 *  - `operator.*`       — operator workbench strings.
 *  - `price.*`          — preformatted price templates for the currency
 *                        token; the number itself is formatted separately
 *                        so the integer minor units never depend on the
 *                        template.
 *  - `consent.*`        — locale-aware locale/destination/consent wording.
 *  - `subject.*`        — confirmation subject prefixes (RTL stress).
 */
export const MESSAGE_KEYS = Object.freeze([
  'nav.discover',
  'nav.gifts',
  'nav.vintage',
  'nav.supplies',
  'nav.search',
  'nav.saved',
  'nav.inbox',
  'nav.cart',
  'nav.orders',
  'nav.collections',
  'nav.registries',
  'nav.agents',
  'nav.security',
  'nav.privacy',
  'nav.overview',
  'nav.listings',
  'nav.orders.production',
  'nav.messages',
  'nav.shop.policies',
  'nav.finance',
  'nav.marketing',
  'nav.team.agents',
  'field.locale',
  'field.destination',
  'field.currency',
  'field.quantity',
  'field.price',
  'field.amount',
  'field.search',
  'field.email',
  'field.password',
  'validation.required',
  'validation.invalid',
  'validation.range',
  'validation.amount',
  'validation.error.summary.title',
  'confirmation.subject',
  'confirmation.review',
  'confirmation.amount',
  'confirmation.currency',
  'confirmation.resource',
  'confirmation.expires',
  'confirmation.cancel',
  'confirmation.confirm',
  'notification.saved',
  'notification.pending',
  'notification.reconcile',
  'notification.error',
  'a11y.busy',
  'a11y.skipToContent',
  'a11y.primaryNavigation',
  'a11y.closeDialog',
  'a11y.togglePassword',
  'operator.context.customer',
  'operator.context.shop',
  'operator.reviewer.confirm',
  'operator.reviewer.reason',
  'operator.queue.urgent',
  'operator.queue.blocked',
  'operator.queue.help',
  'operator.queue.exceptions',
  'operator.queue.growth',
  'price.withCurrency',
  'price.amountOnly',
  'consent.localeNotice',
  'consent.destinationNotice',
  'consent.searchTranslation',
  'consent.originalReachable',
  'subject.confirmationPrefix',
  'subject.confirmationSuffix',
  'recovery.requestReplacement',
  'recovery.retry',
  'recovery.contactSupport',
  'empty.discover',
  'empty.cart',
  'empty.orders',
  'empty.messages',
] as const);
export type MessageKey = (typeof MESSAGE_KEYS)[number];

/** Exactly one translation string per key. */
export type LocalePack = Readonly<Record<MessageKey, string>>;

export const EN_MESSAGES: LocalePack = Object.freeze({
  'nav.discover': 'Discover',
  'nav.gifts': 'Gifts',
  'nav.vintage': 'Vintage',
  'nav.supplies': 'Supplies',
  'nav.search': 'Search',
  'nav.saved': 'Saved items',
  'nav.inbox': 'Inbox',
  'nav.cart': 'Cart',
  'nav.orders': 'Orders',
  'nav.collections': 'Collections',
  'nav.registries': 'Registries',
  'nav.agents': 'Agent connections',
  'nav.security': 'Security',
  'nav.privacy': 'Privacy',
  'nav.overview': 'Overview',
  'nav.listings': 'Listings',
  'nav.orders.production': 'Orders & production',
  'nav.messages': 'Messages',
  'nav.shop.policies': 'Shop & policies',
  'nav.finance': 'Finance',
  'nav.marketing': 'Marketing',
  'nav.team.agents': 'Team & agents',
  'field.locale': 'Language',
  'field.destination': 'Ship to',
  'field.currency': 'Currency',
  'field.quantity': 'Quantity',
  'field.price': 'Price',
  'field.amount': 'Amount',
  'field.search': 'Search creative work',
  'field.email': 'Email',
  'field.password': 'Password',
  'validation.required': 'This field is required.',
  'validation.invalid': 'Enter a valid value.',
  'validation.range': 'Enter a value within the allowed range.',
  'validation.amount': 'Enter an amount in the selected currency.',
  'validation.error.summary.title': 'Check the highlighted fields',
  'confirmation.subject': 'Confirm action',
  'confirmation.review': 'Review what will change',
  'confirmation.amount': 'Amount',
  'confirmation.currency': 'Currency',
  'confirmation.resource': 'Resource',
  'confirmation.expires': 'Expires',
  'confirmation.cancel': 'Cancel',
  'confirmation.confirm': 'Confirm',
  'notification.saved': 'Saved.',
  'notification.pending': 'Still processing. We’ll update this page when it’s done.',
  'notification.reconcile': 'We couldn’t confirm the result yet, so we’re checking it.',
  'notification.error': 'Something went wrong. Reference ID shown below.',
  'a11y.busy': 'Loading',
  'a11y.skipToContent': 'Skip to main content',
  'a11y.primaryNavigation': 'Primary navigation',
  'a11y.closeDialog': 'Close dialog',
  'a11y.togglePassword': 'Show or hide password',
  'operator.context.customer': 'Customer',
  'operator.context.shop': 'Shop',
  'operator.reviewer.confirm': 'I’ve checked this',
  'operator.reviewer.reason': 'Reason code',
  'operator.queue.urgent': 'Urgent',
  'operator.queue.blocked': 'Blocked',
  'operator.queue.help': 'Help requests',
  'operator.queue.exceptions': 'Financial exceptions',
  'operator.queue.growth': 'Growth suggestions',
  'price.withCurrency': '{amount} {currency}',
  'price.amountOnly': '{amount}',
  'consent.localeNotice': 'Changing your language doesn’t change what’s available in your country.',
  'consent.destinationNotice': 'Your delivery country can be different from the country where you live.',
  'consent.searchTranslation': 'Translation shown. Original text is still reachable.',
  'consent.originalReachable': 'Toggle to see the original text.',
  'subject.confirmationPrefix': 'Confirm',
  'subject.confirmationSuffix': '',
  'recovery.requestReplacement': 'Request a replacement from the sender',
  'recovery.retry': 'Retry',
  'recovery.contactSupport': 'Contact support',
  'empty.discover': 'No listings matched. Try another language or delivery country.',
  'empty.cart': 'Your cart is empty. Discover creative work to add an item.',
  'empty.orders': 'You have no orders yet.',
  'empty.messages': 'No messages yet.',
});

export const ES_MESSAGES: LocalePack = Object.freeze({
  'nav.discover': 'Descubrir',
  'nav.gifts': 'Regalos',
  'nav.vintage': 'Vintage',
  'nav.supplies': 'Materiales',
  'nav.search': 'Buscar',
  'nav.saved': 'Guardados',
  'nav.inbox': 'Bandeja de entrada',
  'nav.cart': 'Carrito',
  'nav.orders': 'Pedidos',
  'nav.collections': 'Colecciones',
  'nav.registries': 'Listas de regalos',
  'nav.agents': 'Conexiones de agentes',
  'nav.security': 'Seguridad',
  'nav.privacy': 'Privacidad',
  'nav.overview': 'Resumen',
  'nav.listings': 'Anuncios',
  'nav.orders.production': 'Pedidos y producción',
  'nav.messages': 'Mensajes',
  'nav.shop.policies': 'Tienda y políticas',
  'nav.finance': 'Finanzas',
  'nav.marketing': 'Marketing',
  'nav.team.agents': 'Equipo y agentes',
  'field.locale': 'Idioma',
  'field.destination': 'Enviar a',
  'field.currency': 'Moneda',
  'field.quantity': 'Cantidad',
  'field.price': 'Precio',
  'field.amount': 'Importe',
  'field.search': 'Buscar creaciones',
  'field.email': 'Correo electrónico',
  'field.password': 'Contraseña',
  'validation.required': 'Este campo es obligatorio.',
  'validation.invalid': 'Introduce un valor válido.',
  'validation.range': 'Introduce un valor dentro del rango permitido.',
  'validation.amount': 'Introduce un importe en la moneda seleccionada.',
  'validation.error.summary.title': 'Revisa los campos resaltados',
  'confirmation.subject': 'Confirmar acción',
  'confirmation.review': 'Revisa lo que cambiará',
  'confirmation.amount': 'Importe',
  'confirmation.currency': 'Moneda',
  'confirmation.resource': 'Recurso',
  'confirmation.expires': 'Caduca',
  'confirmation.cancel': 'Cancelar',
  'confirmation.confirm': 'Confirmar',
  'notification.saved': 'Guardado.',
  'notification.pending': 'Todavía se está procesando. Actualizaremos esta página cuando termine.',
  'notification.reconcile': 'Aún no hemos podido confirmar el resultado, así que lo estamos comprobando.',
  'notification.error': 'Algo salió mal. Abajo se muestra el ID de referencia.',
  'a11y.busy': 'Cargando',
  'a11y.skipToContent': 'Saltar al contenido principal',
  'a11y.primaryNavigation': 'Navegación principal',
  'a11y.closeDialog': 'Cerrar diálogo',
  'a11y.togglePassword': 'Mostrar u ocultar la contraseña',
  'operator.context.customer': 'Cliente',
  'operator.context.shop': 'Tienda',
  'operator.reviewer.confirm': 'Lo he comprobado',
  'operator.reviewer.reason': 'Código de motivo',
  'operator.queue.urgent': 'Urgente',
  'operator.queue.blocked': 'Bloqueado',
  'operator.queue.help': 'Solicitudes de ayuda',
  'operator.queue.exceptions': 'Excepciones financieras',
  'operator.queue.growth': 'Sugerencias de crecimiento',
  'price.withCurrency': '{amount} {currency}',
  'price.amountOnly': '{amount}',
  'consent.localeNotice': 'Cambiar el idioma no cambia lo que está disponible en tu país.',
  'consent.destinationNotice': 'Tu país de entrega puede ser distinto del país donde vives.',
  'consent.searchTranslation': 'Traducción mostrada. El texto original sigue accesible.',
  'consent.originalReachable': 'Alternar para ver el texto original.',
  'subject.confirmationPrefix': 'Confirmar',
  'subject.confirmationSuffix': '',
  'recovery.requestReplacement': 'Solicita al remitente una sustitución',
  'recovery.retry': 'Reintentar',
  'recovery.contactSupport': 'Contactar con soporte',
  'empty.discover': 'No hay anuncios que coincidan. Prueba otro idioma u otro país de entrega.',
  'empty.cart': 'Tu carrito está vacío. Descubre creaciones para añadir un artículo.',
  'empty.orders': 'Todavía no tienes pedidos.',
  'empty.messages': 'Todavía no hay mensajes.',
});

export const FR_MESSAGES: LocalePack = Object.freeze({
  'nav.discover': 'Découvrir',
  'nav.gifts': 'Cadeaux',
  'nav.vintage': 'Vintage',
  'nav.supplies': 'Fournitures',
  'nav.search': 'Rechercher',
  'nav.saved': 'Favoris',
  'nav.inbox': 'Boîte de réception',
  'nav.cart': 'Panier',
  'nav.orders': 'Commandes',
  'nav.collections': 'Collections',
  'nav.registries': 'Listes de naissance',
  'nav.agents': 'Connexions d’agents',
  'nav.security': 'Sécurité',
  'nav.privacy': 'Confidentialité',
  'nav.overview': 'Aperçu',
  'nav.listings': 'Annonces',
  'nav.orders.production': 'Commandes et production',
  'nav.messages': 'Messages',
  'nav.shop.policies': 'Boutique et politiques',
  'nav.finance': 'Finance',
  'nav.marketing': 'Marketing',
  'nav.team.agents': 'Équipe et agents',
  'field.locale': 'Langue',
  'field.destination': 'Livrer à',
  'field.currency': 'Devise',
  'field.quantity': 'Quantité',
  'field.price': 'Prix',
  'field.amount': 'Montant',
  'field.search': 'Rechercher des créations',
  'field.email': 'Courriel',
  'field.password': 'Mot de passe',
  'validation.required': 'Ce champ est obligatoire.',
  'validation.invalid': 'Saisissez une valeur valide.',
  'validation.range': 'Saisissez une valeur comprise dans la plage autorisée.',
  'validation.amount': 'Saisissez un montant dans la devise sélectionnée.',
  'validation.error.summary.title': 'Vérifiez les champs en surbrillance',
  'confirmation.subject': 'Confirmer l’action',
  'confirmation.review': 'Vérifiez ce qui va changer',
  'confirmation.amount': 'Montant',
  'confirmation.currency': 'Devise',
  'confirmation.resource': 'Ressource',
  'confirmation.expires': 'Expire',
  'confirmation.cancel': 'Annuler',
  'confirmation.confirm': 'Confirmer',
  'notification.saved': 'Enregistré.',
  'notification.pending': 'Traitement en cours. Nous mettrons cette page à jour une fois terminé.',
  'notification.reconcile': 'Nous n’avons pas encore pu confirmer le résultat, nous le vérifions.',
  'notification.error': 'Une erreur est survenue. Un ID de référence apparaît ci-dessous.',
  'a11y.busy': 'Chargement',
  'a11y.skipToContent': 'Aller au contenu principal',
  'a11y.primaryNavigation': 'Navigation principale',
  'a11y.closeDialog': 'Fermer la boîte de dialogue',
  'a11y.togglePassword': 'Afficher ou masquer le mot de passe',
  'operator.context.customer': 'Client',
  'operator.context.shop': 'Boutique',
  'operator.reviewer.confirm': 'J’ai vérifié',
  'operator.reviewer.reason': 'Code de motif',
  'operator.queue.urgent': 'Urgent',
  'operator.queue.blocked': 'Bloqué',
  'operator.queue.help': 'Demandes d’aide',
  'operator.queue.exceptions': 'Exceptions financières',
  'operator.queue.growth': 'Suggestions de croissance',
  'price.withCurrency': '{amount} {currency}',
  'price.amountOnly': '{amount}',
  'consent.localeNotice': 'Changer de langue ne modifie pas ce qui est disponible dans votre pays.',
  'consent.destinationNotice': 'Votre pays de livraison peut être différent de votre pays de résidence.',
  'consent.searchTranslation': 'Traduction affichée. Le texte original reste accessible.',
  'consent.originalReachable': 'Basculer pour voir le texte original.',
  'subject.confirmationPrefix': 'Confirmer',
  'subject.confirmationSuffix': '',
  'recovery.requestReplacement': 'Demander un remplacement à l’expéditeur',
  'recovery.retry': 'Réessayer',
  'recovery.contactSupport': 'Contacter le support',
  'empty.discover': 'Aucune annonce ne correspond. Essayez une autre langue ou un autre pays de livraison.',
  'empty.cart': 'Votre panier est vide. Découvrez des créations à ajouter.',
  'empty.orders': 'Vous n’avez pas encore de commandes.',
  'empty.messages': 'Aucun message pour le moment.',
});

export const DE_MESSAGES: LocalePack = Object.freeze({
  'nav.discover': 'Entdecken',
  'nav.gifts': 'Geschenke',
  'nav.vintage': 'Vintage',
  'nav.supplies': 'Material',
  'nav.search': 'Suchen',
  'nav.saved': 'Merkliste',
  'nav.inbox': 'Posteingang',
  'nav.cart': 'Warenkorb',
  'nav.orders': 'Bestellungen',
  'nav.collections': 'Sammlungen',
  'nav.registries': 'Geschenklisten',
  'nav.agents': 'Agent-Verbindungen',
  'nav.security': 'Sicherheit',
  'nav.privacy': 'Datenschutz',
  'nav.overview': 'Übersicht',
  'nav.listings': 'Angebote',
  'nav.orders.production': 'Bestellungen und Produktion',
  'nav.messages': 'Nachrichten',
  'nav.shop.policies': 'Shop und Richtlinien',
  'nav.finance': 'Finanzen',
  'nav.marketing': 'Marketing',
  'nav.team.agents': 'Team und Agenten',
  'field.locale': 'Sprache',
  'field.destination': 'Versand nach',
  'field.currency': 'Währung',
  'field.quantity': 'Menge',
  'field.price': 'Preis',
  'field.amount': 'Betrag',
  'field.search': 'Kreative Arbeiten suchen',
  'field.email': 'E-Mail',
  'field.password': 'Passwort',
  'validation.required': 'Dieses Feld ist erforderlich.',
  'validation.invalid': 'Geben Sie einen gültigen Wert ein.',
  'validation.range': 'Geben Sie einen Wert im zulässigen Bereich ein.',
  'validation.amount': 'Geben Sie einen Betrag in der gewählten Währung ein.',
  'validation.error.summary.title': 'Prüfen Sie die markierten Felder',
  'confirmation.subject': 'Aktion bestätigen',
  'confirmation.review': 'Prüfen Sie, was sich ändert',
  'confirmation.amount': 'Betrag',
  'confirmation.currency': 'Währung',
  'confirmation.resource': 'Ressource',
  'confirmation.expires': 'Läuft ab',
  'confirmation.cancel': 'Abbrechen',
  'confirmation.confirm': 'Bestätigen',
  'notification.saved': 'Gespeichert.',
  'notification.pending': 'Wird noch bearbeitet. Wir aktualisieren diese Seite, sobald es fertig ist.',
  'notification.reconcile': 'Wir konnten das Ergebnis noch nicht bestätigen und prüfen es gerade.',
  'notification.error': 'Etwas ist schiefgelaufen. Referenz-ID siehe unten.',
  'a11y.busy': 'Wird geladen',
  'a11y.skipToContent': 'Zum Hauptinhalt springen',
  'a11y.primaryNavigation': 'Hauptnavigation',
  'a11y.closeDialog': 'Dialog schließen',
  'a11y.togglePassword': 'Passwort anzeigen oder ausblenden',
  'operator.context.customer': 'Kunde',
  'operator.context.shop': 'Shop',
  'operator.reviewer.confirm': 'Ich habe das geprüft',
  'operator.reviewer.reason': 'Begründungscode',
  'operator.queue.urgent': 'Dringend',
  'operator.queue.blocked': 'Blockiert',
  'operator.queue.help': 'Hilfeanfragen',
  'operator.queue.exceptions': 'Finanzielle Ausnahmen',
  'operator.queue.growth': 'Wachstumsvorschläge',
  'price.withCurrency': '{amount} {currency}',
  'price.amountOnly': '{amount}',
  'consent.localeNotice': 'Ein Sprachwechsel ändert nicht, was in Ihrem Land verfügbar ist.',
  'consent.destinationNotice': 'Ihr Lieferland kann sich von Ihrem Wohnsitzland unterscheiden.',
  'consent.searchTranslation': 'Übersetzung angezeigt. Der Originaltext bleibt erreichbar.',
  'consent.originalReachable': 'Umschalten, um den Originaltext zu sehen.',
  'subject.confirmationPrefix': 'Bestätigen',
  'subject.confirmationSuffix': '',
  'recovery.requestReplacement': 'Ersatz beim Absender anfordern',
  'recovery.retry': 'Erneut versuchen',
  'recovery.contactSupport': 'Support kontaktieren',
  'empty.discover': 'Keine passenden Angebote. Versuchen Sie eine andere Sprache oder ein anderes Lieferland.',
  'empty.cart': 'Ihr Warenkorb ist leer. Entdecken Sie kreative Arbeiten.',
  'empty.orders': 'Sie haben noch keine Bestellungen.',
  'empty.messages': 'Noch keine Nachrichten.',
});

export const JA_MESSAGES: LocalePack = Object.freeze({
  'nav.discover': '見つける',
  'nav.gifts': 'ギフト',
  'nav.vintage': 'ヴィンテージ',
  'nav.supplies': '素材',
  'nav.search': '検索',
  'nav.saved': '保存したアイテム',
  'nav.inbox': '受信トレイ',
  'nav.cart': 'カート',
  'nav.orders': '注文',
  'nav.collections': 'コレクション',
  'nav.registries': 'ギフトレジストリ',
  'nav.agents': 'エージェント連携',
  'nav.security': 'セキュリティ',
  'nav.privacy': 'プライバシー',
  'nav.overview': '概要',
  'nav.listings': '出品',
  'nav.orders.production': '注文と製造',
  'nav.messages': 'メッセージ',
  'nav.shop.policies': 'ショップとポリシー',
  'nav.finance': '財務',
  'nav.marketing': 'マーケティング',
  'nav.team.agents': 'チームとエージェント',
  'field.locale': '言語',
  'field.destination': '配送先',
  'field.currency': '通貨',
  'field.quantity': '数量',
  'field.price': '価格',
  'field.amount': '金額',
  'field.search': '作品を検索',
  'field.email': 'メールアドレス',
  'field.password': 'パスワード',
  'validation.required': 'この項目は必須です。',
  'validation.invalid': '有効な値を入力してください。',
  'validation.range': '許可された範囲の値を入力してください。',
  'validation.amount': '選択した通貨で金額を入力してください。',
  'validation.error.summary.title': 'ハイライトされた項目を確認してください',
  'confirmation.subject': '操作の確認',
  'confirmation.review': '変更内容を確認してください',
  'confirmation.amount': '金額',
  'confirmation.currency': '通貨',
  'confirmation.resource': 'リソース',
  'confirmation.expires': '有効期限',
  'confirmation.cancel': 'キャンセル',
  'confirmation.confirm': '確認する',
  'notification.saved': '保存しました。',
  'notification.pending': '処理中です。完了したらこのページを更新します。',
  'notification.reconcile': 'まだ結果を確認できないため、現在確認しています。',
  'notification.error': '問題が発生しました。下に参照IDを表示しています。',
  'a11y.busy': '読み込み中',
  'a11y.skipToContent': '本文へスキップ',
  'a11y.primaryNavigation': 'メインナビゲーション',
  'a11y.closeDialog': 'ダイアログを閉じる',
  'a11y.togglePassword': 'パスワードの表示/非表示',
  'operator.context.customer': '顧客',
  'operator.context.shop': 'ショップ',
  'operator.reviewer.confirm': '確認しました',
  'operator.reviewer.reason': '理由コード',
  'operator.queue.urgent': '緊急',
  'operator.queue.blocked': 'ブロック中',
  'operator.queue.help': 'ヘルプ依頼',
  'operator.queue.exceptions': '財務上の例外',
  'operator.queue.growth': '成長提案',
  'price.withCurrency': '{amount} {currency}',
  'price.amountOnly': '{amount}',
  'consent.localeNotice': '言語を切り替えても、お住まいの国で利用できる内容は変わりません。',
  'consent.destinationNotice': '配送先の国は、お住まいの国と異なっていてもかまいません。',
  'consent.searchTranslation': '翻訳を表示しています。原文も参照できます。',
  'consent.originalReachable': '切り替えて原文を表示します。',
  'subject.confirmationPrefix': '確認',
  'subject.confirmationSuffix': '',
  'recovery.requestReplacement': '送り主に代替品を依頼する',
  'recovery.retry': '再試行',
  'recovery.contactSupport': 'サポートに問い合わせる',
  'empty.discover': '該当する出品がありません。別の言語や配送先の国をお試しください。',
  'empty.cart': 'カートは空です。作品を見つけて追加してください。',
  'empty.orders': 'まだ注文はありません。',
  'empty.messages': 'まだメッセージはありません。',
});

export const LOCALE_PACKS: Readonly<Record<LaunchLocale, LocalePack>> = Object.freeze({
  en: EN_MESSAGES,
  es: ES_MESSAGES,
  fr: FR_MESSAGES,
  de: DE_MESSAGES,
  ja: JA_MESSAGES,
});

/**
 * Return the exact frozen pack for a launch locale. Unknown locales throw a
 * fail-closed `LocalePackError` rather than emitting a fallback pack: the
 * UI must not silently present the English copy under a different locale.
 */
export function getLocalePack(locale: string): LocalePack {
  assertLaunchLocale(locale);
  return LOCALE_PACKS[locale];
}

/**
 * Return the exact translation key for a launch locale. A missing key or a
 * key whose value is empty (except the intentional optional subject suffix) throws `LocalePackError`, so a partially
 * translated surface fails closed instead of rendering an empty label.
 */
export function translate(locale: string, key: MessageKey): string {
  const pack = getLocalePack(locale);
  const value = pack[key];
  if (typeof value !== 'string' || (value.length === 0 && key !== 'subject.confirmationSuffix')) {
    throw new LocalePackError(
      'MISSING_TRANSLATION',
      `Missing translation for key ${key} in locale ${locale}`,
    );
  }
  return value;
}

/**
 * Verify that every launch pack declares exactly the reference key set.
 * Missing and unknown keys are both fail-closed, in that order, so a pack
 * that accidentally drops a required key or adds a key not in the reference
 * catalogue is refused. Used by the contract tests at E01.03.
 */
export type LocalePackIssue =
  | { kind: 'missing'; locale: LaunchLocale; key: string }
  | { kind: 'unknown'; locale: LaunchLocale; key: string }
  | { kind: 'empty'; locale: LaunchLocale; key: string };

export function verifyLocalePacks(): readonly LocalePackIssue[] {
  const issues: LocalePackIssue[] = [];
  const reference = new Set<string>(MESSAGE_KEYS);
  for (const locale of LAUNCH_LOCALES) {
    const pack = LOCALE_PACKS[locale];
    const keys = Object.keys(pack);
    for (const key of reference) {
      if (!Object.hasOwn(pack, key)) {
        issues.push({ kind: 'missing', locale, key });
        continue;
      }
      const value = (pack as Record<string, unknown>)[key];
      if (typeof value !== 'string' || (value.length === 0 && key !== 'subject.confirmationSuffix')) {
        issues.push({ kind: 'empty', locale, key });
      }
    }
    for (const key of keys) {
      if (!reference.has(key)) {
        issues.push({ kind: 'unknown', locale, key });
      }
    }
  }
  return Object.freeze(issues);
}

// ---------------------------------------------------------------------------
// Mixed-script and RTL text handling
// ---------------------------------------------------------------------------

const LEFT_TO_RIGHT_ISOLATE = '\u2066';
const POP_DIRECTIONAL_ISOLATE = '\u2069';

/**
 * Locales whose digits and numerals are formatted with `Intl` into the
 * script-native numbering system when ICU is available. For these we isolate
 * the LTR embed itself; the caller decides whether to render the native
 * numerals from `formatNumber`.
 */
export function renderMixedScript(value: string, locale: string): string {
  assertSupportedDirection(locale);
  // Isolate LTR runs (numbers, Latin currency codes, LTR ids) with an
  // explicit LRI...PDI pair so an embedded LTR segment inside RTL text
  // cannot reorder the surrounding glyph sequence. Non-RTL locales are
  // returned verbatim so the helper is safe to call unconditionally.
  if (LOCALE_DIRECTION[locale] === 'ltr') return value;
  return value.replace(
    /([A-Za-z0-9]+(?:[._\-:][A-Za-z0-9]+)*)/g,
    `${LEFT_TO_RIGHT_ISOLATE}$1${POP_DIRECTIONAL_ISOLATE}`,
  );
}

/**
 * A currency-code/LTR-isolated price helper. `formatMoney` uses the locale's
 * `Intl` number formatter with the exact integer minor units so a caller
 * cannot accidentally produce a floating-point approximation. The currency
 * code is placed after the amount on every launch locale (En/Es/Fr/De/Ja),
 * matching the `price.withCurrency` template; the `renderMixedScript`
 * helper ensures the code is isolated from surrounding RTL stress text.
 *
 * The helper never accepts a float. `money.minor` is a signed base-10
 * integer string; USD/EUR use exponent 2 and JPY uses exponent 0,
 * so a caller cannot invent a per-currency exponent here —
 * the qualified currency registry is defined elsewhere.
 */
export type LocaleMoney = { readonly currency: string; readonly minor: string };

export const LOCALE_CURRENCY_EXPONENT: Readonly<Record<string, number>> =
  Object.freeze({ USD: 2, EUR: 2, JPY: 0 });

export function formatMoney(locale: LaunchLocale, money: LocaleMoney): string {
  assertLaunchLocale(locale);
  if (typeof money.currency !== 'string' || !/^[A-Z]{3}$/.test(money.currency)) {
    throw new LocalePackError('INVALID_CURRENCY', `Invalid currency: ${String(money.currency)}`);
  }
  if (typeof money.minor !== 'string' || !/^(?:0|-?[1-9][0-9]*)$/.test(money.minor)) {
    throw new LocalePackError('INVALID_MINOR', `Invalid minor: ${String(money.minor)}`);
  }
  const exponent = LOCALE_CURRENCY_EXPONENT[money.currency];
  if (exponent === undefined) {
    throw new LocalePackError(
      'UNKNOWN_CURRENCY_EXPONENT',
      `No exponent for currency ${money.currency}`,
    );
  }
  const negative = money.minor.startsWith('-');
  const digits = negative ? money.minor.slice(1) : money.minor;
  const padded = exponent === 0 ? digits : digits.padStart(exponent + 1, '0');
  const whole = exponent === 0 ? padded : padded.slice(0, padded.length - exponent);
  const fraction = exponent === 0 ? '' : padded.slice(padded.length - exponent);
  // Format the integer as BigInt, then insert the exact minor fraction into
  // locale-owned parts. Never round large amounts through binary floats.
  const format = new Intl.NumberFormat(locale, {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
    useGrouping: true,
  });
  const signedWhole = negative ? -BigInt(whole) : BigInt(whole);
  const parts = format.formatToParts(signedWhole);
  // BigInt has no negative zero; preserve the sign for sub-unit liabilities.
  if (negative && signedWhole === 0n) {
    const negativeParts = format.formatToParts(-1n);
    const signIndex = negativeParts.findIndex(part => part.type === 'minusSign');
    parts.splice(Math.max(0, signIndex), 0, { type: 'minusSign', value: negativeParts[signIndex].value });
  }
  const numeric = parts.map(part => part.type === 'fraction' ? fraction : part.value).join('');
  const template = translate(locale, 'price.withCurrency');
  return renderMixedScript(
    template.replace('{amount}', numeric).replace('{currency}', money.currency),
    locale,
  );
}

/**
 * Locale-aware number formatting. Uses `Intl.NumberFormat` with the exact
 * locale so grouping/separators follow the pack's conventions. The value
 * must be a safe integer; floats are refused. Timezone is irrelevant.
 */
export function formatNumber(locale: LaunchLocale, value: number): string {
  assertLaunchLocale(locale);
  if (!Number.isSafeInteger(value)) {
    throw new LocalePackError('INVALID_NUMBER', `Value must be a safe integer: ${String(value)}`);
  }
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Locale-aware date formatting. `Intl.DateTimeFormat` is pinned to UTC so
 * tests and screenshots are deterministic; the instant is parsed with
 * `Date.parse` and must be a finite ISO-8601 UTC instant.
 */
export function formatDate(locale: LaunchLocale, instant: string): string {
  assertLaunchLocale(locale);
  const ms = Date.parse(instant);
  if (!Number.isFinite(ms)) {
    throw new LocalePackError('INVALID_INSTANT', `Invalid instant: ${String(instant)}`);
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(ms));
}

/**
 * Mixed-script/RTL critical-flow fixture. The `subject` mixes a Latin
 * currency code and a Latin order id inside a right-to-left label so a UI
 * test can assert the exact glyph sequence without shipping a screenshot as
 * proof. The `rendered` field is the isolated form a caller must actually
 * render; the `expectedIsolated` field records the exact code points.
 */
export type RtlStressFixture = Readonly<{
  locale: RtlStressLocale;
  raw: string;
  rendered: string;
  expectedIsolated: string;
}>;

export function rtlStressFixture(): readonly RtlStressFixture[] {
  const fixtures: RtlStressFixture[] = [];
  // The Arabic fixture contains the exact LTR tokens that would otherwise
  // reorder: the currency code (EUR) and the order id (TG-1042). The raw
  // string places those tokens inside the RTL sentence so a naive renderer
  // produces a visually reordered subject.
  const arabicRaw = 'تأكيد الدفع EUR طلب TG-1042';
  const arabicRendered = renderMixedScript(arabicRaw, 'ar');
  fixtures.push(
    Object.freeze({
      locale: 'ar',
      raw: arabicRaw,
      rendered: arabicRendered,
      expectedIsolated: 'تأكيد الدفع \u2066EUR\u2069 طلب \u2066TG-1042\u2069',
    }),
  );
  const hebrewRaw = 'אישור תשלום EUR הזמנה TG-1042';
  const hebrewRendered = renderMixedScript(hebrewRaw, 'he');
  fixtures.push(
    Object.freeze({
      locale: 'he',
      raw: hebrewRaw,
      rendered: hebrewRendered,
      expectedIsolated: 'אישור תשלום \u2066EUR\u2069 הזמנה \u2066TG-1042\u2069',
    }),
  );
  return Object.freeze(fixtures);
}

/**
 * Verify that the RTL fixture is stable: every fixture must already place
 * an LRI/PDI isolate around each Latin token so a UI renderer cannot reorder
 * the glyphs. A caller that mutates the fixture is caught here.
 */
export function verifyRtlFixtures(): readonly string[] {
  const issues: string[] = [];
  for (const fixture of rtlStressFixture()) {
    if (!fixture.rendered.includes(LEFT_TO_RIGHT_ISOLATE)) {
      issues.push(`${fixture.locale}: missing LRI`);
    }
    if (!fixture.rendered.includes(POP_DIRECTIONAL_ISOLATE)) {
      issues.push(`${fixture.locale}: missing PDI`);
    }
    if (fixture.rendered !== fixture.expectedIsolated) {
      issues.push(`${fixture.locale}: rendered does not match expectedIsolated`);
    }
  }
  return Object.freeze(issues);
}

/**
 * Export the raw isolate code points so a UI can set them explicitly and a
 * test can assert the exact byte sequence without depending on a screenshot.
 */
export const DIRECTIONAL_ISOLATES = Object.freeze({
  lri: LEFT_TO_RIGHT_ISOLATE,
  pdi: POP_DIRECTIONAL_ISOLATE,
});

/**
 * A typed, frozen screen-state key set that every critical flow must handle.
 * The locale packs already cover the message strings for each state; this
 * enum gives the UI a stable name to reference and prevents a caller from
 * silently shipping a critical flow without one of the required states.
 */
export const SCREEN_STATES = Object.freeze([
  'loading',
  'empty',
  'ready',
  'error',
  'denied',
  'pending',
  'stale',
  'success',
] as const);
export type ScreenState = (typeof SCREEN_STATES)[number];

/**
 * A locale-by-locale coverage map for the critical-flow keys the plan names
 * (search/tax/country/errors/agent confirmation). A locale that is missing
 * one of these is a fail-closed bug; the check helper below returns each
 * missing (locale, key) pair so a test can assert zero misses on the pack
 * actually rendered.
 */
export const CRITICAL_FLOW_KEYS: readonly MessageKey[] = Object.freeze([
  'field.search',
  'field.amount',
  'field.destination',
  'validation.amount',
  'validation.error.summary.title',
  'confirmation.subject',
  'confirmation.amount',
  'confirmation.currency',
  'operator.reviewer.confirm',
  'recovery.retry',
]);

export type CriticalFlowMiss = { locale: LaunchLocale; key: MessageKey };

export function verifyCriticalFlowCoverage(): readonly CriticalFlowMiss[] {
  const misses: CriticalFlowMiss[] = [];
  for (const locale of LAUNCH_LOCALES) {
    const pack = LOCALE_PACKS[locale];
    for (const key of CRITICAL_FLOW_KEYS) {
      const value = pack[key];
      if (typeof value !== 'string' || (value.length === 0 && key !== 'subject.confirmationSuffix')) {
        misses.push({ locale, key });
      }
    }
  }
  return Object.freeze(misses);
}
